import { on, createReducer, createFeature } from '@ngrx/store';
import { CheckoutStep } from '@arc/unified-payments-frontend/shared-payment';
import { PaymentFooterActions } from '../actions';
import {
  HostedPaymentsPadResultComponentPadActions,
  HostedPaymentsPaymentDetailsComponentActions,
  HostedPaymentsReviewPaymentComponentActions,
  HostedPaymentsReviewPaymentComponentPaymentActions,
  HostedPaymentsSelectPaymentMethodActions,
  PoaPadResultComponentPadActions,
  PoaPaymentDetailsComponentActions,
  PoaReviewPaymentComponentActions,
  PoaSelectPaymentMethodComponentActions,
  PoiIntlMakePaymentComponentActions,
  PoiIntlStripeResultPaymentComponentActions,
  PoiMakePaymentComponentActions,
  PoiPadResultComponentPadActions,
  PoiReviewPaymentComponentActions,
  PoiSelectPaymentMethodComponentActions,
} from '@arc/unified-payments-frontend/shared-stores';

export interface PaymentFooterStoreState {
  checkoutStep: CheckoutStep;
  currentStepSubmitted: boolean;
  backSubmitted: boolean;
  translations: Record<string, string>;
}

export const initialState: PaymentFooterStoreState = {
  checkoutStep: null,
  currentStepSubmitted: false,
  backSubmitted: false,
  translations: {},
};

export const paymentFooterFeature = createFeature({
  name: 'paymentFooter',
  reducer: createReducer(
    initialState,
    on(
      PaymentFooterActions.ExecuteProceedToPaymentAction,
      PaymentFooterActions.ExecuteReviewPaymentAction,
      PaymentFooterActions.ExecuteAuthorizePaymentAction,
      (state) => ({
        ...state,
        currentStepSubmitted: true,
      }),
    ),
    on(
      PaymentFooterActions.ExecuteBackFromSelectPaymentMethodAction,
      (state) => ({
        ...state,
        backSubmitted: true,
      }),
    ),
    on(
      PaymentFooterActions.CurrentCheckoutStepChangedAction,
      (state, action) => ({
        ...state,
        checkoutStep: action.payload.currentStep,
      }),
    ),
    on(
      PaymentFooterActions.CurrentStepValidationFailed,
      PaymentFooterActions.CurrentCheckoutStepChangedAction,
      PoiSelectPaymentMethodComponentActions.selectedBankAccountMissingPad,
      PoaSelectPaymentMethodComponentActions.selectedBankAccountMissingPad,
      HostedPaymentsSelectPaymentMethodActions.selectedBankAccountMissingPad,
      PoiMakePaymentComponentActions.emailVerificationPendingPopupClosed,
      PoiIntlMakePaymentComponentActions.emailVerificationPendingPopupClosed,
      PoaPaymentDetailsComponentActions.emailVerificationPendingPopupClosed,
      (state) => ({
        ...state,
        currentStepSubmitted: false,
        backSubmitted: false,
      }),
    ),
    on(PaymentFooterActions.GetTranslationsSuccess, (state, action) => ({
      ...state,
      translations: action.translations,
    })),
    on(
      HostedPaymentsPaymentDetailsComponentActions.paymentDetailsStepSubmitted,
      (state, action) => {
        return {
          ...state,
          currentStepSubmitted: false,
          checkoutStep: action.payload.valid
            ? CheckoutStep.PaymentMethod
            : state.checkoutStep,
        };
      },
    ),
    on(
      HostedPaymentsSelectPaymentMethodActions.backToPaymentDetails,
      (state) => {
        return {
          ...state,
          backSubmitted: false,
          checkoutStep: CheckoutStep.PaymentDetails,
        };
      },
    ),
    on(
      HostedPaymentsSelectPaymentMethodActions.proceedToReview,
      (state, action) => {
        return {
          ...state,
          currentStepSubmitted: false,
          checkoutStep: action.selectPaymentMethod.valid
            ? CheckoutStep.Review
            : state.checkoutStep,
        };
      },
    ),
    on(
      HostedPaymentsSelectPaymentMethodActions.closeAddPaymentMethodDialog,
      (state, action) => {
        return {
          ...state,
          checkoutStep: action.payload.hasPaymentAccounts
            ? state.checkoutStep
            : CheckoutStep.PaymentDetails,
        };
      },
    ),
    on(
      HostedPaymentsReviewPaymentComponentActions.reviewPaymentStepSubmitted,
      PoiReviewPaymentComponentActions.reviewPaymentStepSubmitted,
      PoaReviewPaymentComponentActions.reviewPaymentStepSubmitted,
      (state, action) => {
        return {
          ...state,
          currentStepSubmitted: action.event.valid,
        };
      },
    ),
    on(
      PaymentFooterActions.ExecuteBackFromReviewPaymentAction,
      PoaPadResultComponentPadActions.completeTwikeyInviteSuccess,
      PoaPadResultComponentPadActions.completeTwikeyInviteError,
      HostedPaymentsPadResultComponentPadActions.completeTwikeyInviteSuccess,
      HostedPaymentsPadResultComponentPadActions.completeTwikeyInviteError,
      PoiPadResultComponentPadActions.completeTwikeyInviteSuccess,
      PoiPadResultComponentPadActions.completeTwikeyInviteError,
      (state) => {
        return {
          ...state,
          checkoutStep: CheckoutStep.PaymentMethod,
        };
      },
    ),
    on(
      HostedPaymentsReviewPaymentComponentPaymentActions.executePaymentComplete,
      HostedPaymentsReviewPaymentComponentPaymentActions.checkPaymentStatusCompleted,
      (state, action) => {
        return {
          ...state,
          currentStepSubmitted: false,
          checkoutStep: action.paymentComplete
            ? CheckoutStep.Confirmation
            : state.checkoutStep,
        };
      },
    ),
    on(
      HostedPaymentsReviewPaymentComponentPaymentActions.checkPaymentStatusTimedOut,
      PoiIntlStripeResultPaymentComponentActions.paymentCompleted,
      (state) => {
        return {
          ...state,
          checkoutStep: CheckoutStep.Confirmation,
        };
      },
    ),
    on(
      PoiIntlStripeResultPaymentComponentActions.stripeRedirectCompleted,
      (state, action) => {
        return {
          ...state,
          checkoutStep: action.isSuccess
            ? CheckoutStep.Confirmation
            : CheckoutStep.PaymentDetails,
        };
      },
    ),
  ),
});

export const {
  name,
  reducer,
  selectCheckoutStep,
  selectCurrentStepSubmitted,
  selectBackSubmitted,
  selectPaymentFooterState,
  selectTranslations,
} = paymentFooterFeature;
